(ns graph-ui.web.codemirror
  "Reagent wrapper around @uiw/react-codemirror / CodeMirror 6
  (ADR-graph-ui-v0-012-01 Step 1). Falls back to a plain textarea during SSR/node
  tests where js/document is unavailable.

  Two format-specific extension sets:
  - :edn     — indent guides (faint vertical lines at each bracket-indent level)
  - :indent  — widget-property folding (fold service + default-fold on creation)

  The CM6 API calls (ViewPlugin.fromClass, Decoration.line, EditorView.theme,
  foldService.of, foldEffect.of, etc.) live in cm_extensions.js to avoid
  Closure advanced-mode property name mangling."
  (:require [reagent.core :as r]
            [graph-ui.web.theme :as theme]
            ["@uiw/react-codemirror" :default CodeMirror]
            ["codemirror" :refer [basicSetup]]
            ["@codemirror/language" :refer [StreamLanguage]]
            ["@codemirror/legacy-modes/mode/clojure" :refer [clojure]]
            ["@codemirror/legacy-modes/mode/javascript" :refer [javascript json]]
            ["@codemirror/legacy-modes/mode/python" :refer [python]]
            ["@codemirror/legacy-modes/mode/shell" :refer [shell]]
            ["@codemirror/legacy-modes/mode/xml" :refer [html]]
            ["@codemirror/legacy-modes/mode/yaml" :refer [yaml]]
            ["@codemirror/view" :refer [keymap]]
            ["./cm_extensions.js" :as cmh]))

;; @feature F-graph-ui-v0-001-01-ui-quality-system
;; @adr ADR-graph-ui-v0-012-01-mantine-component-library

;; ── A NOTE ON CALLING JS METHODS, WHICH COST A DAY ────────────────────
;;
;; Every call below is written `(.method obj args)`, never `(obj.method args)`.
;; The second form LOOKS like interop and is not. ClojureScript treats `Foo.bar`
;; as a host call only for a namespace such as `js/Foo`; for a name introduced by
;; :refer it resolves `keymap.of` as an ordinary function VALUE and invokes it
;; through the CLJS calling convention, which compiles to:
;;
;;   keymap.of.cljs$core$IFn$_invoke$arity$1 ? … : keymap.of.call(null, arg)
;;
;; — `this` is explicitly null. A static method that never touches `this`
;; (StreamLanguage.define is `return new StreamLanguage(spec)`) survives that
;; unharmed, which is exactly why the mistake reads as correct. A PROTOTYPE
;; method does not: `Facet.of` is `new FacetProvider([], this, …)`, so
;; `(keymap.of …)` produced a provider whose facet was null, and CodeMirror threw
;; `Cannot read properties of null (reading 'extensions')` while flattening the
;; extension set. That took out every surface with a :text-area on it — the
;; `notes` island rendered "Render crashed." and nothing else, and the Studio
;; catalogue unmounted partway through (AC-graph-ui-v0-001-01 §E).
;;
;; Nothing warned. The compiler is content, the shape is plausible, and the
;; failure is a null-pointer read deep inside a dependency. `StreamLanguage.define`
;; below is corrected too, though it was working: it was working by luck.

(def ^:private language-modes
  "Map Meta-UI :language keyword -> CodeMirror StreamLanguage extension.

  The keys are the :language values a partition's content kinds declare
  (dsl/platform/partitions.edn), so this table and that one are two halves of
  one statement: the vault says what a file IS, and this says how to colour it.
  A language with no entry falls back to Clojure rather than failing — a wrong
  colour is a smaller lie than a blank pane.

  Legacy stream modes rather than the Lezer grammars, matching what this file
  already did for Clojure. They are one dependency (@codemirror/legacy-modes,
  already present) instead of six, and for READING a file — which is all
  :code-view does — they are indistinguishable."
  {:clojure    (.define StreamLanguage clojure)
   :edn        (.define StreamLanguage clojure)
   :javascript (.define StreamLanguage javascript)
   :json       (.define StreamLanguage json)
   :python     (.define StreamLanguage python)
   :shell      (.define StreamLanguage shell)
   :html       (.define StreamLanguage html)
   :yaml       (.define StreamLanguage yaml)})

;; ── Indent guides (EDN tab) ───────────────────────────────────────────
;;
;; Faint vertical lines at each 2-space indent level. The ViewPlugin and
;; theme are created in cm_extensions.js to avoid property name mangling.

(def ^:private indent-guides-extension
  "Combined indent-guide extension: ViewPlugin + theme, both created in JS."
  #js [(cmh/createIndentGuidePlugin) (cmh/createIndentGuideTheme)])

;; ── Widget-property folding (indent tab) ──────────────────────────────
;;
;; A fold service that identifies foldable ranges in the bracket-stripped
;; indent format. For each widget header line, the fold range spans from
;; the end of the header line to the start of :children or the next
;; sibling/parent. This hides property lines while keeping the tree skeleton
;; (- :widget, :children, ;; ─┘) visible.

(def ^:private indent-fold-service
  "FoldService facet entry for the indent format, created in JS."
  (cmh/createIndentFoldService))

(defn apply-default-folds!
  "Scan the document for all widget header lines and fold their property
  ranges. Called on editor creation (onCreateEditor) for the indent format
  so the tree skeleton is prominent by default."
  [view]
  (cmh/applyDefaultFolds view))

;; ── Extension assembly ────────────────────────────────────────────────

(defn cm-extensions
  "Extensions for a CodeMirror instance: basic setup + a language mode +
  format-specific extensions (indent guides for :edn, fold service for
  :indent) + an optional Ctrl/Cmd+Enter keybinding.

  PUBLIC so codemirror-test can hand the result to EditorState.create, which is
  where a malformed extension actually blows up. `editor` below cannot be tested
  that way — under node it takes the SSR branch and returns a plain <textarea>,
  which is why every unit test passed while this namespace was producing an
  extension set CodeMirror refused to resolve."
  ([language on-submit format] (cm-extensions language on-submit format nil nil nil))
  ([language on-submit format links on-link-click] (cm-extensions language on-submit format links on-link-click nil))
  ([language on-submit format links on-link-click merge-original]
   (cm-extensions language on-submit format links on-link-click merge-original false))
  ;; `wrap?` (George, 2026-10-05): true wraps a long line onto the next screen
  ;; line. Last, and false in every shorter arity, so no caller that predates
  ;; it changes.
  ([language on-submit format links on-link-click merge-original wrap?]
  (let [lang-ext (get language-modes language (get language-modes :clojure))
        fmt-ext  (case format
                   :edn    indent-guides-extension
                   :indent #js [indent-fold-service]
                   nil)
        base     (cond-> (if fmt-ext
                           (.concat #js [basicSetup lang-ext] fmt-ext)
                           #js [basicSetup lang-ext])
                   ;; The id links of a :code-view. Built in JS for the same
                   ;; reason every other extension here is (property mangling),
                   ;; and appended rather than folded into `format` because a
                   ;; file can want links AND a format-specific extension.
                   (and (seq links) on-link-click)
                   (.concat (cmh/createIdLinkPlugin
                              (clj->js (mapv (fn [l] {:text (:text l)
                                                      :frameId (:frame-id l)})
                                             links))
                              on-link-click))

                   ;; THE CONFLICT VIEW. Built in JS for the same reason as every
                   ;; extension here, and appended LAST so it decorates whatever
                   ;; the language and format already set up.
                   (string? merge-original)
                   (.concat (cmh/createUnifiedMerge merge-original))

                   wrap?
                   (.concat #js [(cmh/lineWrapping)]))]
    (if on-submit
      ;; (.of keymap …), NOT (keymap.of …) — see the note above language-modes.
      (.concat base #js [(.of keymap
                           #js [#js {:key "Ctrl-Enter"
                                     :preventDefault true
                                     :run (fn [_view] (on-submit) true)}
                                #js {:key "Cmd-Enter"
                                     :preventDefault true
                                     :run (fn [_view] (on-submit) true)}])])
      base))))

(defn union-merge
  "A three-way UNION MERGE of one document, as a string.

   Every argument and the result are plain strings, so node-diff3's own object
   shape never crosses into CLJS where advanced compilation would rename its
   keys — the same reason every CM6 call in this namespace goes through
   cm_extensions.js.

   Where the two sides changed different regions, both changes are kept. Where
   they changed the SAME region, the result carries git's own conflict markers,
   because that is the one case where no union exists and a person must read it.

   Pure: the same three texts always give the same result, which is what lets the
   conflict answers be toggles rather than acts (ADR-graph-ui-v0-030-01)."
  [base mine theirs]
  (cmh/unionMerge (or base "") (or mine "") (or theirs "")))

(defn editor
  "A CodeMirror 6 editor as a Reagent component.
   Props:
     :value      - controlled string value
     :on-change  - fn [new-value] called on every edit
     :language   - keyword (:clojure :edn) default :clojure
     :format     - keyword (:edn :indent) for format-specific extensions
                   (:edn gets indent guides, :indent gets fold service)
     :placeholder- string placeholder
     :disabled?  - boolean
     :read-only? - boolean; refuses edits but KEEPS the cursor, so the text can
                   still be selected and copied (a :code-view is read this way)
     :links      - [{:text :frame-id}] rendered as clickable spans in the body
     :on-link-click - fn [frame-id] called when one is clicked
     :class      - extra CSS class string for the wrapper
     :on-submit  - fn [] called on Ctrl/Cmd+Enter
     :on-create-editor - fn [view] called once with the EditorView (for
                         programmatic selection/scroll, e.g. Studio link-select)
     :merge-original - string; when present the editor becomes a UNIFIED MERGE
                       VIEW against it. The doc stays the person's own text and
                       this is what arrived, so each changed chunk carries its
                       own accept (keep mine) and reject (take theirs).
     :wrap?      - boolean; true wraps a long line onto the next screen line."
  [{:keys [value on-change language format placeholder disabled? class on-submit
           on-create-editor read-only? links on-link-click merge-original wrap?]}]
  (if (or (not (exists? js/document)) (not (exists? js/window)))
    ;; SSR / node-test path: render a static textarea so render-to-static-markup
    ;; and tests that don't need the editor surface can still exercise the widget.
    [:textarea
     {:class (or class "")
      :value (or value "")
      :placeholder (or placeholder "")
      :disabled (boolean disabled?)
      :readOnly true}]
    [:> CodeMirror
     {:value (or value "")
      :height "100%"
      :placeholder (or placeholder "")
      ;; :editable and :readOnly are DIFFERENT questions and a source view needs
      ;; both answers. :editable false would also drop the cursor, so text could
      ;; not be selected or copied — which is most of what reading a file is for.
      ;; :readOnly keeps the cursor and refuses the edit.
      :editable (not (boolean disabled?))
      :readOnly (boolean read-only?)
      :theme @theme/mantine-scheme
      :extensions (cm-extensions language on-submit format links on-link-click
                                  merge-original (boolean wrap?))
      :className (or class "")
      :onChange (fn [v _vu] (on-change v))
      :onCreateEditor (when on-create-editor
                        (fn [view _state] (on-create-editor view)))}]))
